import { useMemo, useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { Button } from '@/components/ui/button';
import { type Income } from '@/hooks/admin/shared/useIncome';
import TabErrorBanner from '@/components/v2/molecules/admin/shared/TabErrorBanner';
import IncomeFilters from '@/components/v2/molecules/admin/tabs/AdminIncomeTab/IncomeFilters';
import IncomeTabSkeleton from '@/components/v2/molecules/admin/tabs/AdminIncomeTab/IncomeTabSkeleton';
import DeleteDialog from '@/components/v2/molecules/admin/tabs/shared/DeleteDialog';
import TablePagination from '@/components/v2/molecules/admin/shared/TablePagination';
import { useIncomeFilters } from '@/hooks/admin/tabs/useIncomeFilters';
import useRolePermissions from '@/hooks/admin/shared/useRolePermissions';
import useTransactionModals from '@/hooks/admin/shared/useTransactionModals';
import { useDeleteIncome } from '@/hooks/admin/shared/useIncomeMutations';
import { Plus } from 'lucide-react';
import IncomeFormModal from '../shared/IncomeFormModal';
import IncomeTable from '../AdminIncomeTab/IncomeTable';
import { useToast } from "@/hooks/use-toast";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useContracts } from "@/hooks/useContracts";
import { useShiftSelect } from "@/hooks/useShiftSelect";

interface SelectionState {
  count: number;
  total: number;
  onDelete: () => void;
  onEdit?: () => void;
  isDeleting: boolean;
}

export interface TransactionsIncomeViewRef {
  downloadCSV: () => void;
  createNew: () => void;
  hasData: boolean;
}

interface TransactionsIncomeViewProps {
  onSelectionChange?: (selection: SelectionState | null) => void;
  showOnlySelected?: boolean;
}

const TransactionsIncomeView = forwardRef<TransactionsIncomeViewRef, TransactionsIncomeViewProps>(
  ({ onSelectionChange, showOnlySelected }, ref) => {
  const { canCreate, canEdit, canDelete, role } = useRolePermissions();
  const { toast } = useToast();

  const deleteIncome = useDeleteIncome();
  
  const { data: organizations = [] } = useOrganizations();
  const { data: contracts = [] } = useContracts();

  const {
    itemToEdit: incomeToEdit,
    isModalOpen: isIncomeModalOpen,
    isDeleteDialogOpen: isDeleteIncomeDialogOpen,
    handleNew: handleNewIncome,
    handleEdit: handleEditIncome,
    handleModalClose: handleIncomeModalClose,
    handleDelete: handleDeleteIncome,
    confirmDelete: confirmDeleteIncome,
    setIsDeleteDialogOpen: setIsDeleteIncomeDialogOpen,
  } = useTransactionModals<Income>({
    onDelete: async (id) => await deleteIncome.mutateAsync(id),
  });

  const {
    filteredIncomes,
    paymentTypes,
    isLoading: isLoadingIncomes,
    error: errorIncomes,
    refetch: refetchIncomes,
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    goToPage,
    changePageSize,
    searchTerm,
    selectedProperties,
    selectedOrganizations,
    selectedPaymentType,
    dateFrom,
    dateTo,
    hasActiveFilters,
    setSearchTerm,
    setSelectedProperties,
    setSelectedOrganizations,
    setSelectedPaymentType,
    setDateFrom,
    setDateTo,
    clearFilters,
  } = useIncomeFilters();

  const {
    selectedItems: selectedIncomes,
    selectedItemsData,
    handleSelect: handleSelectIncome,
    handleSelectAll,
    clearSelection,
    getSelectedTotal,
  } = useShiftSelect({ items: filteredIncomes });

  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);

  // Use stored data for accurate total calculation across pages
  const selectedIncomesTotal = useMemo(() => {
    return getSelectedTotal('payout');
  }, [getSelectedTotal]);

  // Filter to show only selected items when filter is active
  const displayedIncomes = useMemo(() => {
    if (showOnlySelected && selectedIncomes.length > 0) {
      // Use stored data instead of filtering current page
      return Array.from(selectedItemsData.values());
    }
    return filteredIncomes;
  }, [filteredIncomes, selectedIncomes, selectedItemsData, showOnlySelected]);

  const handleDownloadCSV = () => {
    const incomesToExport = selectedIncomes.length > 0
      ? filteredIncomes.filter(income => selectedIncomes.includes(income.id))
      : filteredIncomes;

    const csvHeaders = [
      'Reservación',
      'Nombre de Huésped',
      'Propiedad',
      'Tipo de ingreso',
      'Tipo de pago',
      'Pago bruto',
      'IVA',
      'Ret IVA',
      'Ret ISR',
      'Comisión',
      'Pago Recibido',
      'Fecha de pago',
      'Requiere Factura',
    ];

    const csvData = incomesToExport.map((income) => [
      income.booking_id || 'N/A',
      income.guest_name || 'N/A',
      income.property_short_name || 'N/A',
      income.income_type || 'N/A',
      income.payment_type || 'N/A',
      income.gross_payment?.toString() || '0',
      income.iva?.toString() || '0',
      income.ret_iva?.toString() || '0',
      income.ret_isr?.toString() || '0',
      income.comission?.toString() || '0',
      income.payout?.toString() || '0',
      income.payout_date || 'N/A',
      income.fiscal_invoice ? 'Sí' : 'No',
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map((row) => row.map((field) => `"${field}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `ingresos_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: 'CSV descargado',
      description: `Se han exportado ${incomesToExport.length} ingresos`,
    });
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    downloadCSV: handleDownloadCSV,
    createNew: handleNewIncome,
    hasData: filteredIncomes.length > 0,
  }));

  const handleDeleteSelected = () => {
    setIsBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    setIsDeleting(true);
    try {
      await Promise.all(
        selectedIncomes.map(incomeId => 
          deleteIncome.mutateAsync(incomeId)
        )
      );

      toast({
        title: "Éxito",
        description: `Se eliminaron ${selectedIncomes.length} ingreso(s) correctamente`,
      });

      clearSelection();
      setIsBulkDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting selected incomes:", error);
      toast({
        title: "Error",
        description: "Error al eliminar los ingresos seleccionados",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Notify parent of selection changes
  useEffect(() => {
    if (selectedIncomes.length > 0) {
      onSelectionChange?.({
        count: selectedIncomes.length,
        total: selectedIncomesTotal,
        onDelete: handleDeleteSelected,
        isDeleting,
      });
    } else {
      onSelectionChange?.(null);
    }
  }, [selectedIncomes.length, selectedIncomesTotal, isDeleting, onSelectionChange]);

  if (isLoadingIncomes) {
    return <IncomeTabSkeleton />;
  }

  if (errorIncomes) {
    return (
      <TabErrorBanner
        title="Error al cargar ingresos"
        message="No se pudieron cargar los ingresos. Por favor, intenta nuevamente."
        onRetry={() => refetchIncomes()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <IncomeFilters
        dateFrom={dateFrom}
        dateTo={dateTo}
        hasActiveFilters={hasActiveFilters}
        paymentTypes={paymentTypes}
        role={role}
        searchTerm={searchTerm}
        selectedProperties={selectedProperties}
        selectedOrganizations={selectedOrganizations}
        selectedPaymentType={selectedPaymentType}
        organizations={organizations}
        contracts={contracts}
        clearFilters={clearFilters}
        setDateFrom={setDateFrom}
        setDateTo={setDateTo}
        setSearchTerm={setSearchTerm}
        setSelectedProperties={setSelectedProperties}
        setSelectedOrganizations={setSelectedOrganizations}
        setSelectedPaymentType={setSelectedPaymentType}
      />

      {!filteredIncomes || filteredIncomes.length === 0 ? (
        <div className="bg-muted/50 border border-border rounded-lg p-8 text-center flex flex-col items-center">
          <h3 className="text-foreground font-medium mb-2">No hay ingresos</h3>
          <p className="text-muted-foreground text-sm mb-4">
            No se encontraron ingresos que coincidan con los filtros aplicados.
          </p>
          {canCreate && (
            <Button
              onClick={handleNewIncome}
              className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Crear Ingreso
            </Button>
          )}
        </div>
      ) : (
        <>
          <IncomeTable
            incomes={displayedIncomes}
            onEdit={handleEditIncome}
            onDelete={handleDeleteIncome}
            canEdit={canEdit}
            canDelete={canDelete}
            selectedIncomes={selectedIncomes}
            onSelectIncome={handleSelectIncome}
            onSelectAll={handleSelectAll}
          />

          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={goToPage}
            onPageSizeChange={changePageSize}
            pageSizeOptions={[100, 500]}
          />
        </>
      )}

      <IncomeFormModal
        isOpen={isIncomeModalOpen}
        onClose={handleIncomeModalClose}
        editingIncome={incomeToEdit}
      />

      <DeleteDialog
        title="¿Estás seguro?"
        description="Esta acción no se puede deshacer. Se eliminará permanentemente el registro de ingreso."
        open={isDeleteIncomeDialogOpen}
        onOpenChange={setIsDeleteIncomeDialogOpen}
        onConfirm={confirmDeleteIncome}
      />

      <DeleteDialog
        title={`¿Eliminar ${selectedIncomes.length} ingreso(s)?`}
        description={`Esta acción no se puede deshacer. Se eliminarán permanentemente ${selectedIncomes.length} registro(s) de ingreso.`}
        open={isBulkDeleteDialogOpen}
        onOpenChange={setIsBulkDeleteDialogOpen}
        onConfirm={confirmBulkDelete}
        isLoading={isDeleting}
      />
    </div>
  );
});

TransactionsIncomeView.displayName = 'TransactionsIncomeView';

export default TransactionsIncomeView;